// MOD-F2/MOD-F3 (2026-09-28, REPORTERIA-REGLAS §9) — exportación de la bandeja de Administración ›
// Moderación, que hasta ahora no tenía botón de exportar. El libro lleva LAS MISMAS columnas que la
// tabla y en el mismo orden (`COLUMNAS_BANDEJA`); la única que no viaja es «Acciones», que solo
// existe en pantalla.
//
// Texto LITERAL en español (encabezados, estado, campaña), mismo criterio que el resto de los libros
// de Reportería: el archivo no cambia con el idioma de quien lo exporta. Los encabezados salen de las
// MISMAS claves i18n que la tabla, con el locale fijo en español, así que no pueden desalinearse.
import type { TFunction } from 'i18next';
import { exportMultiSheetExcel } from '@/utils/exportExcelMultiSheet';
import type { ExcelCellValue, ExcelReportSpec } from '@/utils/exportExcelMultiSheet';

/** Columnas de la bandeja, en el orden de la tabla (sin «Acciones»). Claves de `admin.moderation.*`. */
export const COLUMNAS_BANDEJA = [
  'colReportedBy',
  'colRecognitionFrom',
  'colTo',
  'colDetail',
  'colReason',
  'colDate',
  'colCampaign',
  'colStatus',
] as const;

export type ColumnaBandeja = (typeof COLUMNAS_BANDEJA)[number];

/** Una fila ya resuelta a texto (nombres, estado, campaña y fecha). */
export type FilaBandeja = Record<ColumnaBandeja, string>;

const NOMBRE_ARCHIVO = 'moderacion-bandeja';
const FILA_ENCABEZADO = 3;

/**
 * Arma el libro (una hoja). `tEs` es `i18n.getFixedT('es', 'recognition')`: los encabezados son las
 * mismas claves que pinta la tabla. `filtros` describe, en español, lo que la pantalla tiene puesto;
 * sin él, el archivo se leería como la bandeja completa aunque venga acotada.
 */
export function libroDeLaBandeja(
  filas: readonly FilaBandeja[],
  tEs: TFunction,
  filtros: readonly string[],
): ExcelReportSpec {
  const subtitulo = filtros.length > 0
    ? `Reportes de la bandeja de moderación. Filtros aplicados: ${filtros.join(' · ')}.`
    : 'Reportes de la bandeja de moderación, sin filtros.';
  const rows: ExcelCellValue[][] = [
    ['Bandeja de reportes de moderación'],
    [subtitulo],
    [],
    COLUMNAS_BANDEJA.map((c) => tEs(`admin.moderation.${c}`)),
    ...filas.map((fila) => COLUMNAS_BANDEJA.map((c) => fila[c])),
  ];
  return {
    fileName: NOMBRE_ARCHIVO,
    sheets: [{
      name: 'Reportes',
      rows,
      styleHints: {
        headerRows: [FILA_ENCABEZADO],
        columnWidths: [24, 24, 28, 44, 32, 14, 26, 14],
      },
    }],
  };
}

/** Descarga el libro. Separado de `libroDeLaBandeja` para poder probar el contenido sin descargar. */
export function exportarBandeja(
  filas: readonly FilaBandeja[],
  tEs: TFunction,
  filtros: readonly string[],
): Promise<void> {
  return exportMultiSheetExcel(libroDeLaBandeja(filas, tEs, filtros));
}
